import React, { useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { Shield, AlertTriangle, Clock } from 'lucide-react'
import type { RootState } from '../../store'
import { BRAND } from '../../config/brand'

const MFA_GRACE_PERIOD_DAYS = 7

export const MFAEnforcementBanner: React.FC = () => {
  // MFA enforcement is currently disabled for all users.
  // To re-enable, remove this early return.
  return null

  const { user } = useSelector((state: RootState) => state.auth)
  const navigate = useNavigate()
  const location = useLocation()
  const [mfaStatus, setMfaStatus] = useState<{
    mfa_enabled: boolean
    created_at: string | null
    mfa_enforcement_exempt?: boolean
  } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  
  // Fetch actual MFA status from API to ensure we have the latest
  useEffect(() => {
    const checkMfaStatus = async () => {
      try {
        const response = await fetch('/api/mfa/status', { credentials: 'include' })
        if (response.ok) {
          const data = await response.json()
          setMfaStatus({
            mfa_enabled: data.mfa_enabled,
            created_at: data.created_at || user?.created_at || null,
            mfa_enforcement_exempt: Boolean(data.mfa_enforcement_exempt),
          })
        }
      } catch (error) {
        console.error('Error checking MFA status:', error)
      } finally {
        setIsLoading(false)
      }
    }
    
    checkMfaStatus()
  }, [user])
  
  // Calculate grace period status
  const gracePeriodStatus = useMemo(() => {
    // Still loading, don't show anything
    if (isLoading) {
      return { showBanner: false, daysRemaining: 0, isExpired: false }
    }
    
    // If MFA is enabled (from API or user state), don't show banner
    if (mfaStatus?.mfa_enabled || user?.mfa_enabled) {
      return { showBanner: false, daysRemaining: 0, isExpired: false }
    }

    if (mfaStatus?.mfa_enforcement_exempt) {
      return { showBanner: false, daysRemaining: 0, isExpired: false }
    }

    // Get created_at from API response or user state
    const createdAt = mfaStatus?.created_at || user?.created_at
    
    if (!createdAt) {
      // If no created_at, assume account is old and grace period expired
      return { showBanner: true, daysRemaining: 0, isExpired: true }
    }
    
    const createdDate = new Date(createdAt)
    const now = new Date()
    const diffTime = now.getTime() - createdDate.getTime()
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    const daysRemaining = MFA_GRACE_PERIOD_DAYS - diffDays
    
    return {
      showBanner: true,
      daysRemaining: Math.max(0, daysRemaining),
      isExpired: daysRemaining <= 0
    }
  }, [user, mfaStatus, isLoading])
  
  // Don't show on MFA-related pages or while loading
  const isMFAPage = location.pathname.includes('/mfa')
  
  if (isLoading || !gracePeriodStatus.showBanner || isMFAPage) {
    return null
  }
  
  // If grace period expired, show blocking overlay
  if (gracePeriodStatus.isExpired) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
        <div className="bg-surface rounded-2xl shadow-2xl max-w-lg w-full p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-bad/10 rounded-full flex items-center justify-center">
            <Shield className="w-10 h-10 text-bad" />
          </div>
          
          <h2 className="text-2xl font-bold text-primary mb-3">
            Two-Factor Authentication Required
          </h2>
          
          <p className="text-secondary mb-6">
            Your 7-day grace period has ended. To continue using {BRAND.assistant} and protect your personal data, 
            you must enable two-factor authentication now.
          </p>
          
          <div className="bg-warn/10 border border-warn/30 rounded-lg p-4 mb-6 text-left">
            <h4 className="font-semibold text-warn mb-2">Why is this required?</h4>
            <ul className="text-sm text-warn space-y-1">
              <li>• Protects your dreams, goals, and personal reflections</li>
              <li>• Prevents unauthorized access to your account</li>
              <li>• Industry-standard security practice</li>
            </ul>
          </div>
          
          <button
            onClick={() => navigate('/mfa-setup')}
            className="w-full py-4 px-6 bg-brand hover:bg-brand-hover text-on-brand font-semibold rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
          >
            <Shield className="w-5 h-5" />
            Enable Two-Factor Authentication
          </button>
          
          <p className="text-xs text-muted mt-4">
            This takes less than 2 minutes using Google Authenticator or any TOTP app
          </p>
        </div>
      </div>
    )
  }
  
  // Show countdown banner for users still in grace period
  return (
    <div className={`sticky top-0 z-40 ${
      gracePeriodStatus.daysRemaining <= 2 
        ? 'bg-bad' 
        : 'bg-warn'
    } text-on-brand shadow-lg`}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {gracePeriodStatus.daysRemaining <= 2 ? (
              <AlertTriangle className="w-5 h-5 animate-pulse" />
            ) : (
              <Clock className="w-5 h-5" />
            )}
            <div>
              <span className="font-semibold">
                {gracePeriodStatus.daysRemaining === 0 
                  ? 'Last day to enable 2FA!' 
                  : gracePeriodStatus.daysRemaining === 1
                  ? '1 day left to enable Two-Factor Authentication'
                  : `${gracePeriodStatus.daysRemaining} days left to enable Two-Factor Authentication`
                }
              </span>
              <span className="hidden sm:inline ml-2 opacity-90">
                Secure your account before access is restricted.
              </span>
            </div>
          </div>
          
          <button
            onClick={() => navigate('/mfa-setup')}
            className="px-4 py-1.5 bg-surface/20 hover:bg-surface/30 rounded-lg font-medium text-sm transition-colors flex items-center gap-2"
          >
            <Shield className="w-4 h-4" />
            Enable Now
          </button>
        </div>
      </div>
    </div>
  )
}

export default MFAEnforcementBanner

